import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { concatMap, filter, take, catchError } from 'rxjs/operators';
import { AuthState } from 'src/app/store/auth/auth.reducer';
import { received401Action, received403Action } from 'src/app/store/auth/auth.actions';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private tokenRefreshSubmitting: boolean = false;
  private tokenRefreshSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private skipUrls: Array<string> = ['auth/me', 'auth/login', 'forgotpassword', 'resetpassword', 'signup'];

  constructor(
    private store: Store<{ auth: AuthState }>,
    private authService: AuthService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Third-party/asset requests (Azure Blob, i18n files, ...) must not receive the JWT
    // or trigger the refresh-token flow
    if (!UnauthorizedInterceptor.isApiRequest(req.url)) return next.handle(req);

    if (this.skipUrls.some(path => req.url.endsWith(path))) return this.unauthorizedPipe(req, next);

    if (this.tokenRefreshSubmitting)
      return this.tokenRefreshSubject.pipe(
        filter(res => res === true),
        take(1),
        concatMap(() => {
          const request = req.clone({
            setHeaders: {
              Authorization: `Bearer ${AuthService.getJwt()}`,
            },
          });
          return this.unauthorizedPipe(request, next);
        }),
      );

    this.tokenRefreshSubmitting = true;
    return this.authService.refreshToken().pipe(
      concatMap(refreshResponse => {
        this.tokenRefreshSubmitting = false;
        this.tokenRefreshSubject.next(true); // unblock queued requests
        const request = req.clone({
          setHeaders: {
            Authorization: `Bearer ${AuthService.getJwt()}`,
          },
        });
        return this.unauthorizedPipe(request, next);
      }),
      catchError((err: HttpErrorResponse) => {
        // Always reset state and unblock queued requests even on failure
        this.tokenRefreshSubmitting = false;
        this.tokenRefreshSubject.next(true);

        if (err && err.status === 403) {
          this.store.dispatch(received403Action());
        }
        if (err && err.status === 401) {
          this.store.dispatch(received401Action());
        }
        return throwError(err);
      }),
    );
  }

  static isApiRequest(url: string): boolean {
    if (url.startsWith(environment.apiBase)) return true;
    try {
      return environment.jwtWhitelistedDomains.includes(new URL(url, window.location.origin).host);
    } catch {
      return false;
    }
  }

  unauthorizedPipe(req: HttpRequest<any>, next: HttpHandler) {
    return next.handle(req).pipe(
      catchError((errorEvent: HttpErrorResponse) => {
        if (errorEvent && errorEvent.status === 401) {
          this.store.dispatch(received401Action());
        }
        return throwError(errorEvent);
      }),
    );
  }
}
